import {useState} from 'react';
import {useIntl} from 'react-intl';
import {isInValidEmailFormat} from '../../../shared/util/EmailUtil';
import type {ProjectOption} from '../ClientContactModal';

const MAX_STANDARD = 191;
const MAX_PHONE = 45;

export type UseClientContactFormReturn = {
	firstName: string;
	handleFirstNameChange: (val: string) => void;
	firstNameError: string;
	lastName: string;
	handleLastNameChange: (val: string) => void;
	lastNameError: string;
	email: string;
	handleEmailChange: (val: string) => void;
	emailError: string;
	handleEmailBlur: () => void;
	jobTitle: string;
	handleJobTitleChange: (val: string) => void;
	phone: string;
	handlePhoneChange: (val: string) => void;
	handleGuestUserToggle: () => void;
	selectedProjectKeys: string[];
	availableProjects: ProjectOption[];
	selectedProjects: ProjectOption[];
	handleAddProject: (projectId: string) => void;
	handleRemoveProject: (projectId: string) => void;
	resetProjects: () => void;
	validateRequired: () => boolean;
	handleSubmit: () => void;
};

export const useClientContactForm = (
	projectOptions: ProjectOption[],
	isGuestUser: boolean,
	onGuestUserToggle: () => void
): UseClientContactFormReturn => {
	const {formatMessage} = useIntl();

	const [firstName, setFirstName] = useState('');
	const [firstNameError, setFirstNameError] = useState('');
	const [lastName, setLastName] = useState('');
	const [lastNameError, setLastNameError] = useState('');
	const [email, setEmail] = useState('');
	const [emailError, setEmailError] = useState('');
	const [jobTitle, setJobTitle] = useState('');
	const [phone, setPhone] = useState('');
	const [selectedProjectKeys, setSelectedProjectKeys] = useState<string[]>([]);

	const handleFirstNameChange = (val: string) => {
		setFirstName(val.slice(0, MAX_STANDARD));
		if (firstNameError) setFirstNameError('');
	};

	const handleLastNameChange = (val: string) => {
		setLastName(val.slice(0, MAX_STANDARD));
		if (lastNameError) setLastNameError('');
	};

	const handleEmailChange = (val: string) => {
		setEmail(val.slice(0, MAX_STANDARD));
		if (emailError) setEmailError('');
	};

	const handleEmailBlur = () => {
		if (email && !isInValidEmailFormat(email)) {
			setEmailError(formatMessage({id: 'client_contact.error.invalid_email'}));
		} else {
			setEmailError('');
		}
	};

	const handleJobTitleChange = (val: string) => setJobTitle(val.slice(0, MAX_STANDARD));
	const handlePhoneChange = (val: string) => setPhone(val.slice(0, MAX_PHONE));

	const resetProjects = () => setSelectedProjectKeys([]);

	const handleGuestUserToggle = () => {
		if (isGuestUser) resetProjects();
		onGuestUserToggle();
	};

	const handleAddProject = (projectId: string) => {
		if (!projectId) return;
		setSelectedProjectKeys(prev => (prev.includes(projectId) ? prev : [...prev, projectId]));
	};

	const handleRemoveProject = (projectId: string) => {
		setSelectedProjectKeys(prev => prev.filter(k => k !== projectId));
	};

	const availableProjects = projectOptions.filter(p => !selectedProjectKeys.includes(p.id));
	const selectedProjects = projectOptions.filter(p => selectedProjectKeys.includes(p.id));

	const validateRequired = (): boolean => {
		const fnErr = firstName.trim() === '' ? formatMessage({id: 'client_contact.error.first_name_required'}) : '';
		const lnErr = lastName.trim() === '' ? formatMessage({id: 'client_contact.error.last_name_required'}) : '';
		const emErr =
			email.trim() === ''
				? formatMessage({id: 'client_contact.error.email_required'})
				: !isInValidEmailFormat(email)
				? formatMessage({id: 'client_contact.error.invalid_email'})
				: '';
		setFirstNameError(fnErr);
		setLastNameError(lnErr);
		setEmailError(emErr);
		return !fnErr && !lnErr && !emErr;
	};

	const handleSubmit = () => {
		if (!validateRequired()) return;
		// Submit wiring deferred to IGNITE-468.
	};

	return {
		firstName,
		handleFirstNameChange,
		firstNameError,
		lastName,
		handleLastNameChange,
		lastNameError,
		email,
		handleEmailChange,
		emailError,
		handleEmailBlur,
		jobTitle,
		handleJobTitleChange,
		phone,
		handlePhoneChange,
		handleGuestUserToggle,
		selectedProjectKeys,
		availableProjects,
		selectedProjects,
		handleAddProject,
		handleRemoveProject,
		resetProjects,
		validateRequired,
		handleSubmit,
	};
};
